import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Dialog } from 'functional-foundations'
import { DeviceInterface, useThemeContext } from 'providers'
import { Typography, Button } from 'foundations-library/components'
import { LoaderDialog, ErrorDialog } from 'functional-foundations'
import { redirectLogin } from 'phonecom'
import { useAccountSecurityContext } from 'providers'
import { DialogType } from 'functional-foundations/types'
import Device from './../components/session-management/Device'
import { getRemoteValue } from '../utils'

const jss = () => ({
    confirm: {
        '& .dialog-container': {
            padding: '40px 40px 0px 40px'
        },
        '& .dialog-content': {
            display: 'inline-flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'normal',
            gap: '40px'
        },
        '& .button-container': {
            display: 'flex',
            padding: '20px 0px 40px 0px',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: '20px',
            alignSelf: 'stretch'
        }
    },
    confirmed: {
        '& .dialog-container': {
            padding: '40px 30px'
        },
        '& .dialog-content': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '60px'
        },
        '& .header': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '50px'
        },
        '& .content': {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '40px',
            '& .devices': { width: '100%' }
        }
    },
    confirmedIncludingCurrentDevice: {
        '& .dialog-container': {
            padding: '40px 30px'
        },
        '& .dialog-content': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '80px'
        },
        '& .header': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '50px'
        },
        '& .content': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '15px'
        }
    }
})

const useStyles = makeStyles(jss)

type SessionSignOutProps = {
    setIsOpen: (isOpen: boolean) => void
    selectedDevices?: DeviceInterface[]
    reloadDevices?: () => Promise<void>
    isCurrentDeviceIncluded: boolean
    logout?: () => any
    onRedirectToLogin?: () => void
} & DialogType

/**
 *
 */
const SessionSignOut = ({ setIsOpen, selectedDevices, reloadDevices, isCurrentDeviceIncluded, logout, onRedirectToLogin, ...dialogProps }: SessionSignOutProps) => {
    const classes = useStyles()
    const theme = useThemeContext()
    const { clearDeviceSessions, clearSessions, reset } = useAccountSecurityContext()

    const getDefaultState = () => {
        if (clearSessions.data && isCurrentDeviceIncluded) return 'confirmed_including_current_device'
        else if (clearSessions.data) return 'confirmed'
        else return 'confirm'
    }
    const [state, setState] = useState<'confirm' | 'confirmed' | 'confirmed_including_current_device'>(getDefaultState())

    const texts = getRemoteValue('session_sign_out')

    const onClose = () => {
        setIsOpen(false)
        Promise.resolve().then(() => {
            reset('CLEAR_SESSIONS')
            setState('confirm')
        })
    }

    const onConfirm = async () => {
        try {
            const data = await clearDeviceSessions(isCurrentDeviceIncluded, logout)
            if (data) {
                if (isCurrentDeviceIncluded) setState('confirmed_including_current_device')
                else setState('confirmed')
            }
        } catch (error) {
            console.error('Failed to clear device sessions:', error)
        }
    }

    const onFinalConfirm = () => {
        onRedirectToLogin ? onRedirectToLogin() : redirectLogin()
    }

    const handleReturnToActiveSessions = () => {
        reloadDevices?.()
        onClose()
    }

    const confirmScreen = () => (
        <>
            <Typography variant='h4' color='neutral-900' align='center'>{selectedDevices!.length === 1 ? texts[state].singular_title : texts[state].plural_title}</Typography>
            <div>
                {
                    selectedDevices!.map(device => <Device device={device} key={device.id} />)
                }
            </div>
            <div className='button-container'>
                <Button size='large' variant='outlined' color='neutral' onClick={onClose}>{texts[state].cancel}</Button>
                <Button size='large' variant='contained' color='primary' onClick={onConfirm}>{texts[state].button}</Button>
            </div>
        </>
    )

    const confirmedScreen = () => (
        <>
            <div className='header'>
                <img src={theme?.icons?.identityAreaLogo} alt='brand logo' style={{ width: '100px', height: '100px' }} />
                <Typography variant='h4' color='neutral-900' align='center'>{selectedDevices!.length === 1 ? texts[state].singular_title : texts[state].plural_title}</Typography>
            </div>
            <div className='content'>
                <div className='devices'>
                    {
                        selectedDevices!.map(device => <Device device={device} key={device.id} />)
                    }
                </div>
                <Button size='large' variant='contained' color='primary' onClick={handleReturnToActiveSessions} icon={'go'}>{texts[state].button}</Button>
            </div>
        </>
    )

    const confirmedIncludingCurrentDeviceScreen = () => (
        <>
            <div className='header'>
                <img src={theme?.icons?.identityAreaLogo} alt='brand logo' style={{ width: '100px', height: '100px' }} />
                <div className='content'>
                    <Typography variant='h4' color='neutral-900' align='center'>{texts[state].title}</Typography>
                    <Typography variant='h8' color='neutral-900' align='center'>{texts[state].subtitle}</Typography>
                </div>
            </div>
            <Button size='large' variant='contained' color='primary' onClick={onFinalConfirm} icon={'go'}>{texts[state].button}</Button>
        </>
    )

    if (clearSessions.isLoading) return <LoaderDialog {...dialogProps} title='Clearing device sessions' subtitle='Please wait!' />

    if (clearSessions.error) return <ErrorDialog {...dialogProps} title='Error' subtitle={clearSessions.error} onClose={onClose} />

    return (
        <Dialog className={state === 'confirm' ? classes.confirm : state === 'confirmed' ? classes.confirmed : classes.confirmedIncludingCurrentDevice} {...dialogProps} data-testid='session-sign-out-dialog'>
            {state === 'confirm' && confirmScreen()}
            {state === 'confirmed' && confirmedScreen()}
            {state === 'confirmed_including_current_device' && confirmedIncludingCurrentDeviceScreen()}
        </Dialog>
    )
}

export default SessionSignOut
